import { useState, useEffect } from "react";
import Seo from '../components/Seo';
import { motion } from "framer-motion";
import {
	FiBook,
	FiGlobe,
	FiShield,
	FiSettings,
	FiCheck,
	FiAlertCircle,
	FiClock,
	FiCopy,
	FiCode,
	FiCheckCircle,
	FiX,
	FiUnlock,
	FiInfo,
	FiExternalLink,
	FiActivity,
	FiZap,
	FiFilter,
	FiAlertTriangle,
} from "react-icons/fi";

const Documentation = ({ serviceName }) => {
	const [activeTab, setActiveTab] = useState("setup");
	const [copiedText, setCopiedText] = useState("");

	const tabs = [
		{ id: "setup", label: "Подключение сайта", icon: <FiGlobe /> },
		{ id: "protection", label: "Вкладка Защита", icon: <FiShield /> },
		{ id: "configuration", label: "Вкладка Конфигурация", icon: <FiSettings /> }
	];

	const copyToClipboard = async (text, description) => {
		try {
			await navigator.clipboard.writeText(text);
			setCopiedText(description);
			setTimeout(() => setCopiedText(""), 2000);
		} catch (err) {
			console.error('Failed to copy:', err);
		}
	};

	useEffect(() => {
		const hash = window.location.hash.replace('#', '');
		if (hash && tabs.some(tab => tab.id === hash)) {
			setActiveTab(hash);
		}
	}, []);

	const setupSteps = [
		{
			id: "preparation",
			title: "1. Подготовка",
			description: "Перед подключением сайта необходимо настроить доступность вашего сервера для внешних подключений.",
			content: (
				<div className="space-y-6">
					<div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
						<div className="flex items-start gap-3">
							<FiShield className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
							<div>
								<h4 className="font-semibold text-blue-300 mb-2">Рекомендация по безопасности</h4>
								<p className="text-sm text-blue-200/80">
									Наша система автоматически шифрует трафиик от браузеров клиентов до нашей сети, но для максимальной защиты мы рекомендуем использовать HTTPS соединение между нашей сетью и вашим бекендом.
									Это обеспечит шифрование всего трафика и предотвратит возможные атаки "человек посередине".
								</p>
							</div>
						</div>
					</div>

					<div className="space-y-4">
						<h4 className="text-lg font-semibold">Требования к бекенду</h4>
						<p className="text-gray-300">
							Ваш сервер должен быть доступен для внешних подключений. Есть два варианта настройки:
						</p>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
								<div className="flex items-center gap-2 mb-3">
									<div className="w-3 h-3 bg-green-500 rounded-full"></div>
									<h5 className="font-semibold">Вариант A: HTTPS соединение (рекомендуется)</h5>
								</div>
								<p className="text-sm text-gray-300 mb-2">
									Ваш бекенд должен принимать HTTPS-запросы. Для этого нужен SSL-сертификат.
								</p>
								<div className="space-y-2 text-sm text-gray-400">
									<div className="flex items-start gap-2">
										<div className="w-4 h-4 bg-green-500/20 rounded flex items-center justify-center mt-0.5 flex-shrink-0">
											<FiCheck className="w-2 h-2 text-green-400" />
										</div>
										<span>Используйте Let's Encrypt или другой доверенный SSL-сертификат</span>
									</div>
									<div className="flex items-start gap-2">
										<div className="w-4 h-4 bg-green-500/20 rounded flex items-center justify-center mt-0.5 flex-shrink-0">
											<FiCheck className="w-2 h-2 text-green-400" />
										</div>
										<span>Или скачайте наш доверенный сертификат из личного кабинета после активации домена</span>
									</div>
								</div>
							</div>

							<div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
								<div className="flex items-center gap-2 mb-3">
									<div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
									<h5 className="font-semibold">Вариант B: HTTP соединение</h5>
								</div>
								<p className="text-sm text-gray-300 mb-2">
									Система также поддерживает подключение по HTTP, но этот вариант менее безопасен.
								</p>
								<div className="space-y-2 text-sm text-gray-400">
									<div className="flex items-start gap-2">
										<FiAlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
										<span>Трафик между нашей сетью и вашим бекендом не шифруется</span>
									</div>
									<div className="flex items-start gap-2">
										<FiAlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
										<span>Рекомендуется только для тестовых сред или внутренних сетей</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			)
		},
		{
			id: "verification",
			title: "2. Подтверждение прав на домен",
			description: "После подготовки сервера необходимо подтвердить права на домен через личный кабинет.",
			content: (
				<div className="space-y-6">
					<div className="space-y-4">
						<h4 className="text-lg font-semibold">Добавление домена в систему</h4>
						<ol className="space-y-4 pl-5 list-decimal">
							<li className="pl-2">
								<p className="font-medium mb-1">Нажмите кнопку "Добавить домен" в личном кабинете</p>
								<p className="text-gray-300 text-sm">Расположена в разделе "Домены" или на главной панели</p>
							</li>
							<li className="pl-2">
								<p className="font-medium mb-1">Введите данные домена:</p>
								<div className="bg-gray-800 rounded-lg p-4 mt-2">
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<div>
											<label className="text-sm text-gray-400 block mb-1">Название домена</label>
											<div className="font-mono text-green-400">example.com</div>
										</div>
										<div>
											<label className="text-sm text-gray-400 block mb-1">IP-адрес бекенда</label>
											<div className="font-mono text-green-400">92.218.147.163</div>
										</div>
									</div>
								</div>
							</li>
							<li className="pl-2">
								<p className="font-medium mb-1">Перейдите во вкладку "Аккаунт" и нажмите "Подтвердить" рядом с доменом</p>
							</li>
							<li className="pl-2">
								<p className="font-medium mb-1">Добавьте TXT-запись в DNS вашего домена</p>
								<div className="mt-2">
									<div className="bg-gray-800 rounded-lg p-4">
										<p className="text-gray-300 mb-3">
											Система сгенерирует уникальную TXT-запись для вашего домена. Скопируйте её и добавьте в DNS вашего домена:
										</p>
										<div className="font-mono text-sm">
											<div className="text-gray-400 mb-1">Пример того, что вы получите:</div>
											<div className="text-green-400">shieldai-verification=abc123def456</div>
										</div>
										<div className="mt-3 text-sm text-gray-400">
											<div>⚠️ Для каждого домена генерируется уникальный токен!</div>
											<div>Не используйте примеры из документации - они не сработают.</div>
										</div>
									</div>
								</div>
							</li>
						</ol>
					</div>

					<div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
						<div className="flex items-start gap-3">
							<FiClock className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
							<div>
								<h4 className="font-semibold text-blue-300 mb-2">Примечание о времени обновления DNS</h4>
								<p className="text-sm text-blue-200/80 mb-2">
									Обновление DNS записей может занять от нескольких минут до нескольких часов.
									Время зависит от TTL (Time To Live) — параметра, указывающего, как долго кэшировать запись.
								</p>
								<p className="text-sm text-blue-200/80">
									После добавления записи система автоматически обнаружит её в течение 5-60 минут и подтвердит домен.
								</p>
							</div>
						</div>
					</div>
				</div>
			)
		},
		{
			id: "activation",
			title: "3. Активация защиты",
			description: "После верификации домена можно активировать защиту и направить трафик через нашу систему.",
			content: (
				<div className="space-y-6">
					<div className="space-y-4">
						<h4 className="text-lg font-semibold">Активация защиты</h4>
						<ol className="space-y-4 pl-5 list-decimal">
							<li className="pl-2">
								<p className="font-medium mb-1">После верификации домена нажмите кнопку "Активировать" рядом с доменом</p>
							</li>
							<li className="pl-2">
								<p className="font-medium mb-1">Измените A-записи в DNS вашего домена</p>
								<div className="bg-gray-800 rounded-lg p-4 mt-2">
									<p className="text-gray-300 mb-3">
										Система предоставит IP-адресa, на которые нужно направить ваш домен. Замените текущую A-запись вашего домена на эти IP.
									</p>
									<div className="mt-3 text-sm text-red-300">
										⚠️ Обязательно удалите старые A-записи перед добавлением новой, чтобы избежать конфликтов!
									</div>
								</div>
							</li>
							<li className="pl-2">
								<p className="font-medium mb-1">Добавьте CNAME-запись, предоставленную сиситемой в DNS домена</p>
							</li>
							<li className="pl-2">
								<p className="font-medium mb-1">Дождитесь обновления DNS (обычно 5-60 минут)</p>
							</li>
						</ol>
					</div>

					<div className="space-y-4">
						<h4 className="text-lg font-semibold">Настройка SSL-сертификатов (если используете HTTPS)</h4>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<div className="bg-green-500/10 border border-green-500/30 rounded-xl p-5">
								<div className="flex items-center gap-3 mb-3">
									<FiGlobe className="w-6 h-6 text-green-400" />
									<h5 className="text-lg font-bold">Если используете публичный сертификат</h5>
								</div>
								<p className="text-sm text-gray-300 mb-4">
									Убедитесь, что ваш SSL-сертификат (Let's Encrypt, Comodo и т.д.) установлен и работает корректно.
									Наша система будет устанавливать HTTPS соединение с вашим бекендом.
								</p>
								<div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mt-2">
									<p className="text-sm text-yellow-300">
										⚠️ Обратите внимание: после подключения домена к нашей системе, автопродление Let's Encrypt
										может не работать, так как домен теперь направлен на наши сервера.
									</p>
								</div>
							</div>

							<div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-5">
								<div className="flex items-center gap-3 mb-3">
									<FiShield className="w-6 h-6 text-blue-400" />
									<h5 className="text-lg font-bold">Наш доверенный сертификат</h5>
								</div>
								<p className="text-sm text-gray-300 mb-4">
									После активации домена в личном кабинете станет доступен для скачивания сертификат,
									подписанный нашим доверенным центром сертификации.
								</p>
								<ol className="space-y-2 text-sm text-gray-300 pl-5 list-decimal">
									<li>Перейдите в раздел "Домены" → выберите ваш домен</li>
									<li>Нажмите "Скачать SSL-сертификат"</li>
									<li>Установите скачанные файлы на ваш сервер</li>
								</ol>
							</div>
						</div>
					</div>

					<div className="space-y-4">
						<h4 className="text-lg font-semibold">Что вы получите после активации</h4>

						<div className="bg-gray-800/50 rounded-xl p-5">
							<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
								<div className="bg-black/30 p-4 rounded-lg">
									<div className="flex items-center gap-2 mb-3">
										<FiShield className="w-5 h-5 text-green-400" />
										<h5 className="font-semibold">Для HTTPS подключения</h5>
									</div>
									<div className="space-y-2">
										<div className="font-mono text-sm">
											<code className="text-green-400">yourdomain.crt</code>
											<p className="text-xs text-gray-400 mt-1">Сертификат для вашего домена</p>
										</div>
										<div className="font-mono text-sm">
											<code className="text-green-400">yourdomain.key</code>
											<p className="text-xs text-gray-400 mt-1">Приватный ключ</p>
										</div>
									</div>
									<p className="text-xs text-gray-400 mt-3">
										Корневой сертификат нашего CA уже установлен во всех наших прокси-серверах.
										Вам нужно установить только эти два файла.
									</p>
								</div>

								<div className="bg-black/30 p-4 rounded-lg">
									<div className="flex items-center gap-2 mb-3">
										<FiZap className="w-5 h-5 text-blue-400" />
										<h5 className="font-semibold">Мгновенная активация</h5>
									</div>
									<p className="text-sm text-gray-300">
										После установки сертификата и обновления DNS ваш сайт сразу начнет получать защищенный трафик через нашу сеть.
									</p>
								</div>

								<div className="bg-black/30 p-4 rounded-lg">
									<div className="flex items-center gap-2 mb-3">
										<FiActivity className="w-5 h-5 text-purple-400" />
										<h5 className="font-semibold">Мониторинг и аналитика</h5>
									</div>
									<p className="text-sm text-gray-300">
										В личном кабинете появится статистика по трафику, заблокированным атакам и производительности вашего сайта.
									</p>
								</div>
							</div>
						</div>
					</div>

					<div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
						<div className="flex items-start gap-3">
							<FiCheck className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
							<div>
								<h4 className="font-semibold text-green-300 mb-2">Завершение настройки</h4>
								<p className="text-sm text-green-200/80 mb-2">
									После обновления DNS и установки сертификата (если используете HTTPS) ваш сайт будет полностью защищен.
									Весь трафик теперь проходит через нашу систему защиты.
								</p>
								<div className="mt-3 space-y-2">
									<div className="flex items-center gap-2">
										<FiCheck className="w-4 h-4 text-green-400" />
										<span className="text-sm">DDoS защита активирована</span>
									</div>
									<div className="flex items-center gap-2">
										<FiCheck className="w-4 h-4 text-green-400" />
										<span className="text-sm">Трафик направляется через нашу сеть</span>
									</div>
									<div className="flex items-center gap-2">
										<FiCheck className="w-4 h-4 text-green-400" />
										<span className="text-sm">Боты и атаки фильтруются в реальном времени</span>
									</div>
									<div className="flex items-center gap-2">
										<FiCheck className="w-4 h-4 text-green-400" />
										<span className="text-sm">Аналитика доступна в личном кабинете</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			)
		},
		{
			id: "cdn",
			title: "4. CDN и кеширование",
			description: "После активации трафик проходит через защищенную CDN сеть, что ускоряет загрузку контента и снижает нагрузку на ваш сервер.",
			content: (
				<div className="space-y-6">
					<div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
						<div className="flex items-start gap-3">
							<FiZap className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
							<div>
								<h4 className="font-semibold text-purple-300 mb-2">Автоматическое ускорение через CDN</h4>
								<p className="text-sm text-purple-200/80">
									После подключения домена весь трафик автоматически начинает проходить через распределенную CDN сеть.
									Это значительно ускоряет доставку контента пользователям по всему миру.
								</p>
							</div>
						</div>
					</div>

					<div className="space-y-4">
						<h4 className="text-lg font-semibold">Как работает CDN кеширование</h4>
						<p className="text-gray-300">
							Наша CDN сеть автоматически кеширует контент на ближайших к пользователю серверах.
							Для управления кешированием используйте стандартные HTTP-заголовки на вашем бекенде.
						</p>

						<div className="bg-green-500/10 border border-green-500/30 rounded-xl p-5">
							<div className="flex items-center gap-3 mb-3">
								<FiSettings className="w-6 h-6 text-green-400" />
								<h5 className="text-lg font-bold">Управление кешированием</h5>
							</div>
							<p className="text-sm text-gray-300 mb-4">
								Вы можете тонко настроить кеширование через заголовки Cache-Control на вашем сервере:
							</p>
							<div className="space-y-3">
								<div className="relative">
									<button
										onClick={() => copyToClipboard(
											`Cache-Control: public, max-age=86400`,
											"Заголовок для статики"
										)}
										className="absolute right-2 top-2 p-2 hover:bg-white/10 rounded-lg transition-colors"
									>
										<FiCopy className="w-4 h-4" />
									</button>
									<div className="bg-black/30 rounded-lg p-3 font-mono text-sm">
										<code className="text-green-400">Cache-Control: public, max-age=86400</code>
										<p className="text-xs text-gray-400 mt-1">Кеширование на 24 часа</p>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="space-y-4">
						<h4 className="text-lg font-semibold">Примеры настройки кеширования</h4>

						<div className="space-y-4">
							<div>
								<h5 className="font-medium text-blue-300 mb-2">Для nginx:</h5>
								<div className="relative">
									<div className="absolute right-3 top-3">
										<button
											onClick={() => copyToClipboard(
												`location ~* \\.(css|js|png|jpg|jpeg|gif|svg|woff|woff2|ico)$ {
    add_header Cache-Control "public, max-age=86400";
}`,
												"Конфигурация nginx для кеширования"
											)}
											className="p-2 hover:bg-white/10 rounded-lg transition-colors"
										>
											<FiCopy className="w-4 h-4" />
										</button>
									</div>
									<div className="bg-gray-800 rounded-lg p-4 font-mono text-sm overflow-x-auto">
										<div className="text-gray-300">location ~* \\.(css|js|png|jpg|jpeg|gif|svg|woff|woff2|ico)$ {'{'}</div>
										<div className="ml-4 text-gray-300">add_header Cache-Control "public, max-age=86400";</div>
										<div className="text-gray-300">{'}'}</div>
									</div>
								</div>
							</div>

							<div>
								<h5 className="font-medium text-blue-300 mb-2">Для Apache:</h5>
								<div className="relative">
									<div className="absolute right-3 top-3">
										<button
											onClick={() => copyToClipboard(
												`<FilesMatch "\\.(css|js|png|jpg|jpeg|gif|svg)$">
    Header set Cache-Control "public, max-age=86400"
</FilesMatch>`,
												"Конфигурация Apache для кеширования"
											)}
											className="p-2 hover:bg-white/10 rounded-lg transition-colors"
										>
											<FiCopy className="w-4 h-4" />
										</button>
									</div>
									<div className="bg-gray-800 rounded-lg p-4 font-mono text-sm overflow-x-auto">
										<div className="text-gray-300">&lt;FilesMatch "\\.(css|js|png|jpg|jpeg|gif|svg)$"&gt;</div>
										<div className="ml-4 text-gray-300">Header set Cache-Control "public, max-age=86400"</div>
										<div className="text-gray-300">&lt;/FilesMatch&gt;</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="space-y-4">
						<h4 className="text-lg font-semibold">Управление кешем CDN</h4>

						<div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-5">
							<div className="flex items-start gap-3">
								<FiAlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
								<div>
									<h5 className="font-semibold text-yellow-300 mb-2">Сброс кеша CDN</h5>
									<p className="text-sm text-yellow-200/80 mb-3">
										При необходимости сбросить кеш CDN для всего сайта или отдельных файлов, обратитесь в нашу техническую поддержку.
									</p>
									<div className="space-y-2">
										<div className="flex items-start gap-2">
											<FiInfo className="w-4 h-4 text-yellow-300 mt-0.5" />
											<span className="text-sm">Сброс кеша выполняется вручную нашей командой</span>
										</div>
										<div className="flex items-start gap-2">
											<FiInfo className="w-4 h-4 text-yellow-300 mt-0.5" />
											<span className="text-sm">Обычно выполняется в течение 15-30 минут</span>
										</div>
										<div className="flex items-start gap-2">
											<FiInfo className="w-4 h-4 text-yellow-300 mt-0.5" />
											<span className="text-sm">Можно сбросить кеш для конкретных URL или всего домена</span>
										</div>
									</div>
									<div className="mt-4">
										<a
											href="https://t.me/Shield_AI"
											className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/50 rounded-lg font-medium transition-colors"
										>
											<FiExternalLink className="w-4 h-4" />
											Обратиться в поддержку для сброса кеша
										</a>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="space-y-4">
						<h4 className="text-lg font-semibold">Преимущества CDN сети</h4>

						<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
							<div className="bg-white/5 rounded-xl p-4">
								<div className="flex items-center gap-3 mb-3">
									<div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
										<FiZap className="w-5 h-5 text-blue-400" />
									</div>
									<h5 className="font-semibold">Скорость</h5>
								</div>
								<p className="text-sm text-gray-300">
									Контент доставляется с ближайшего к пользователю сервера, что сокращает время загрузки на 30-50%.
								</p>
							</div>

							<div className="bg-white/5 rounded-xl p-4">
								<div className="flex items-center gap-3 mb-3">
									<div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
										<FiShield className="w-5 h-5 text-green-400" />
									</div>
									<h5 className="font-semibold">Надежность</h5>
								</div>
								<p className="text-sm text-gray-300">
									Распределенная сеть обеспечивает отказоустойчивость и доступность даже при высокой нагрузке.
								</p>
							</div>

							<div className="bg-white/5 rounded-xl p-4">
								<div className="flex items-center gap-3 mb-3">
									<div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
										<FiActivity className="w-5 h-5 text-purple-400" />
									</div>
									<h5 className="font-semibold">Экономия</h5>
								</div>
								<p className="text-sm text-gray-300">
									Кеширование снижает нагрузку на ваш сервер до 70%, что позволяет экономить на ресурсах хостинга.
								</p>
							</div>
						</div>
					</div>

					<div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
						<div className="flex items-start gap-3">
							<FiCheck className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
							<div>
								<h4 className="font-semibold text-green-300 mb-2">CDN активирован автоматически</h4>
								<p className="text-sm text-green-200/80">
									После активации домена CDN сеть начинает работать автоматически.
									Вам не нужно выполнять дополнительные настройки — просто используйте стандартные заголовки Cache-Control
									для управления кешированием, если это требуется.
								</p>
								<div className="mt-3 space-y-2">
									<div className="flex items-center gap-2">
										<FiCheck className="w-4 h-4 text-green-400" />
										<span className="text-sm">Глобальная сеть доставки контента активирована</span>
									</div>
									<div className="flex items-center gap-2">
										<FiCheck className="w-4 h-4 text-green-400" />
										<span className="text-sm">Автоматическое кеширование статических файлов</span>
									</div>
									<div className="flex items-center gap-2">
										<FiCheck className="w-4 h-4 text-green-400" />
										<span className="text-sm">Защита от перегрузок сервера</span>
									</div>
									<div className="flex items-center gap-2">
										<FiCheck className="w-4 h-4 text-green-400" />
										<span className="text-sm">Ускорение загрузки для пользователей по всему миру</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			)
		}
	];

	const renderTabContent = () => {
		switch (activeTab) {
			case "setup":
				return (
					<div className="space-y-12">
						<div className="space-y-6">
							<h3 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
								Подключение сайта
							</h3>
							<p className="text-gray-300 text-lg">
								Подробная инструкция по подключению вашего сайта к системе защиты {serviceName}.
								Следуйте шагам последовательно для успешной настройки.
							</p>
						</div>

						<div className="space-y-8">
							{setupSteps.map((step, index) => (
								<motion.div
									key={step.id}
									initial={{ opacity: 0, y: 20 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ delay: index * 0.1 }}
									className="bg-white/5 backdrop-blur-lg rounded-3xl p-8 border border-white/10"
								>
									<div className="flex items-start gap-4 mb-6">
										<div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl">
											<span className="text-xl font-bold">{index + 1}</span>
										</div>
										<div>
											<h4 className="text-2xl font-bold mb-2">{step.title}</h4>
											<p className="text-gray-300">{step.description}</p>
										</div>
									</div>
									{step.content}
								</motion.div>
							))}
						</div>

						<div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-3xl p-8 border border-white/10">
							<div className="text-center">
								<h4 className="text-2xl font-bold mb-4">Нужна помощь с настройкой?</h4>
								<p className="text-gray-300 mb-6">
									Наши специалисты готовы помочь с подключением вашего сайта
								</p>
								<a
									href="/dashboard"
									className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-purple-500 hover:to-blue-500 rounded-xl font-semibold transition-all duration-300 hover:scale-105"
								>
									<FiExternalLink className="w-5 h-5" />
									Перейти в личный кабинет
								</a>
							</div>
						</div>
					</div>
				);

			case "protection":
				return (
					<div className="space-y-12">
						<div className="space-y-6">
							<h3 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
								Вкладка Защита
							</h3>
							<p className="text-gray-300 text-lg">
								Центр управления безопасностью вашего сайта. После подключения домена в этой вкладке
								отображаются последние заблокированные запросы и подозрительные IP-адреса.
							</p>
						</div>

						{/* Обзор */}
						<div className="bg-white/5 backdrop-blur-lg rounded-3xl p-8 border border-white/10">
							<h4 className="text-2xl font-bold mb-6">Обзор вкладки Защита</h4>
							<p className="text-gray-300 mb-4">
								Вкладка "Защита" предоставляет полный контроль над безопасностью вашего сайта.
								Здесь вы можете:
							</p>
							<ul className="list-disc pl-5 space-y-2 text-gray-300">
								<li>Просматривать заблокированные атаки в реальном времени</li>
								<li>Управлять подозрительными IP-адресами</li>
								<li>Анализировать детали каждой атаки</li>
								<li>Добавлять IP в белые и черные списки</li>
								<li>Принимать решения по временно заблокированным IP</li>
							</ul>
						</div>

						{/* Заблокированные запросы */}
						<div className="bg-white/5 backdrop-blur-lg rounded-3xl p-8 border border-white/10">
							<h4 className="text-2xl font-bold mb-6">Таблица "Заблокированные запросы"</h4>

							<div className="space-y-6">
								<div>
									<h5 className="text-lg font-semibold mb-3">Назначение</h5>
									<p className="text-gray-300">
										Отображает все запросы, которые были автоматически заблокированы системой защиты.
										Каждая запись содержит полную информацию об атаке.
									</p>
								</div>

								<div>
									<h5 className="text-lg font-semibold mb-3">Столбцы таблицы</h5>
									<div className="space-y-3">
										<div className="flex items-start gap-3">
											<div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
											<div>
												<span className="font-medium">IP-адрес</span>
												<p className="text-gray-400 text-sm">IP-адрес источника атаки</p>
											</div>
										</div>
										<div className="flex items-start gap-3">
											<div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
											<div>
												<span className="font-medium">Метод</span>
												<p className="text-gray-400 text-sm">HTTP метод запроса (GET, POST, PUT и т.д.)</p>
											</div>
										</div>
										<div className="flex items-start gap-3">
											<div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
											<div>
												<span className="font-medium">Причина блокировки</span>
												<p className="text-gray-400 text-sm mb-3">Системная причина блокировки запроса:</p>

												<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
													<div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
														<div className="flex items-center gap-2 mb-2">
															<div className="w-3 h-3 bg-green-500 rounded-full"></div>
															<code className="font-mono text-green-400">bot</code>
														</div>
														<p className="text-sm text-gray-300">Запрос от бота или скрипта, не прошедшего проверку на человечность</p>
													</div>

													<div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
														<div className="flex items-center gap-2 mb-2">
															<div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
															<code className="font-mono text-yellow-400">rate_limit</code>
														</div>
														<p className="text-sm text-gray-300">Превышение лимита запросов с одного IP-адреса в единицу времени</p>
													</div>

													<div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
														<div className="flex items-center gap-2 mb-2">
															<div className="w-3 h-3 bg-red-500 rounded-full"></div>
															<code className="font-mono text-red-400">rules</code>
														</div>
														<p className="text-sm text-gray-300">Нарушение черных списков IP и стран, выставленных пользователем во вкладке "Конфигурация"</p>
													</div>
												</div>
											</div>
										</div>
										<div className="flex items-start gap-3">
											<div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
											<div>
												<span className="font-medium">Количество запросов</span>
												<p className="text-gray-400 text-sm">Количество заблокированных запросов от данного IP</p>
											</div>
										</div>
										<div className="flex items-start gap-3">
											<div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
											<div>
												<span className="font-medium">Время</span>
												<p className="text-gray-400 text-sm">Дата и время блокировки запроса</p>
											</div>
										</div>
									</div>
								</div>

								<div>
									<h5 className="text-lg font-semibold mb-3">Доступные действия</h5>
									<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
										<div className="bg-white/5 rounded-xl p-4">
											<div className="flex items-center gap-3 mb-2">
												<FiInfo className="w-5 h-5 text-blue-400" />
												<span className="font-medium">Детали запроса</span>
											</div>
											<p className="text-sm text-gray-400">
												Полная информация о заблокированном запросе: заголовки, полезная нагрузка,
												геолокация IP-адреса, провайдер и другие детали.
											</p>
										</div>
										<div className="bg-white/5 rounded-xl p-4">
											<div className="flex items-center gap-3 mb-2">
												<FiCheckCircle className="w-5 h-5 text-green-400" />
												<span className="font-medium">Добавить в белый список</span>
											</div>
											<p className="text-sm text-gray-400">
												Разрешить доступ с этого IP-адреса навсегда. Используйте для доверенных IP
												(сотрудники, партнеры, легитимные сервисы).
											</p>
										</div>
										<div className="bg-white/5 rounded-xl p-4">
											<div className="flex items-center gap-3 mb-2">
												<FiX className="w-5 h-5 text-red-400" />
												<span className="font-medium">Добавить в черный список</span>
											</div>
											<p className="text-sm text-gray-400">
												Заблокировать IP-адрес навсегда. Используйте для явных источников атак,
												спама или повторных нарушителей.
											</p>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Подозрительные IP */}
						<div className="bg-white/5 backdrop-blur-lg rounded-3xl p-8 border border-white/10">
							<h4 className="text-2xl font-bold mb-6">Таблица "Подозрительные IP"</h4>

							<div className="space-y-6">
								<div>
									<h5 className="text-lg font-semibold mb-3">Назначение</h5>
									<p className="text-gray-300">
										Отображает IP-адреса, которые демонстрируют подозрительное поведение, но еще не
										заблокированы окончательно. Система временно блокирует такие адреса
										и ожидает вашего решения.
									</p>
									<div className="mt-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
										<p className="text-yellow-200 text-sm">
											<span className="font-medium">Важно:</span> Подозрительные IP блокируются временно
											и ждут вашего решения. Вы можете добавить их в белый список, черный список или
											убрать из временной блокировки.
										</p>
									</div>
								</div>

								<div>
									<h5 className="text-lg font-semibold mb-3">Столбцы таблицы</h5>
									<div className="space-y-3">
										<div className="flex items-start gap-3">
											<div className="w-2 h-2 bg-yellow-400 rounded-full mt-2 flex-shrink-0"></div>
											<div>
												<span className="font-medium">IP-адрес</span>
												<p className="text-gray-400 text-sm">Подозрительный IP-адрес</p>
											</div>
										</div>
										<div className="flex items-start gap-3">
											<div className="w-2 h-2 bg-yellow-400 rounded-full mt-2 flex-shrink-0"></div>
											<div>
												<span className="font-medium">Тип угрозы</span>
												<p className="text-gray-400 text-sm">DDoS, Brute Force, Scanner, Spam</p>
											</div>
										</div>
										<div className="flex items-start gap-3">
											<div className="w-2 h-2 bg-yellow-400 rounded-full mt-2 flex-shrink-0"></div>
											<div>
												<span className="font-medium">Уровень угрозы</span>
												<p className="text-gray-400 text-sm">Оценка опасности (Низкий, Средний, Высокий)</p>
											</div>
										</div>
										<div className="flex items-start gap-3">
											<div className="w-2 h-2 bg-yellow-400 rounded-full mt-2 flex-shrink-0"></div>
											<div>
												<span className="font-medium">Время блокировки</span>
												<p className="text-gray-400 text-sm">Таймер истечения срока блокировки</p>
											</div>
										</div>
									</div>
								</div>

								<div>
									<h5 className="text-lg font-semibold mb-3">Доступные действия</h5>
									<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
										<div className="bg-white/5 rounded-xl p-4">
											<div className="flex items-center gap-3 mb-2">
												<FiInfo className="w-5 h-5 text-blue-400" />
												<span className="font-medium">Информация об IP</span>
											</div>
											<p className="text-sm text-gray-400">
												Подробная информация об IP-адресе: геолокация, провайдер, история активности,
												шаблоны поведения и предыдущие блокировки.
											</p>
										</div>
										<div className="bg-white/5 rounded-xl p-4">
											<div className="flex items-center gap-3 mb-2">
												<FiCheckCircle className="w-5 h-5 text-green-400" />
												<span className="font-medium">Добавить в белый список</span>
											</div>
											<p className="text-sm text-gray-400">
												Разрешить доступ с этого IP. Используйте если уверены, что это легитимный пользователь.
											</p>
										</div>
										<div className="bg-white/5 rounded-xl p-4">
											<div className="flex items-center gap-3 mb-2">
												<FiX className="w-5 h-5 text-red-400" />
												<span className="font-medium">Добавить в черный список</span>
											</div>
											<p className="text-sm text-gray-400">
												Заблокировать IP навсегда. Используйте для явных атакующих или спамеров.
											</p>
										</div>
										<div className="bg-white/5 rounded-xl p-4">
											<div className="flex items-center gap-3 mb-2">
												<FiUnlock className="w-5 h-5 text-yellow-400" />
												<span className="font-medium">Убрать блокировку</span>
											</div>
											<p className="text-sm text-gray-400">
												Снять временную блокировку. IP будет разблокирован, но останется под наблюдением.
											</p>
										</div>
									</div>
								</div>

								<div className="bg-gray-800/50 rounded-xl p-5">
									<h6 className="font-semibold mb-3 text-yellow-300">Как работают временные блокировки</h6>
									<p className="text-gray-300 text-sm">
										Когда система обнаруживает подозрительное поведение (например, множество неудачных попыток входа),
										она автоматически блокирует IP-адрес на определенное время (обычно 24 часа).
										Это предотвращает дальнейшие атаки, но дает вам возможность проанализировать ситуацию и принять решение:
									</p>
									<ul className="list-disc pl-5 mt-2 space-y-1 text-gray-300 text-sm">
										<li>Если это легитимный пользователь — добавьте в белый список</li>
										<li>Если это злоумышленник — добавьте в черный список</li>
										<li>Если не уверены — оставьте временную блокировку или уберите ее</li>
									</ul>
								</div>
							</div>
						</div>

						{/* Рекомендации */}
						<div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-3xl p-8 border border-white/10">
							<h4 className="text-2xl font-bold mb-6">Рекомендации по использованию</h4>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
								<div>
									<h5 className="text-lg font-semibold mb-4 flex items-center gap-2">
										<FiCheckCircle className="w-5 h-5 text-green-400" />
										Когда добавлять в белый список
									</h5>
									<ul className="space-y-3">
										<li className="flex items-start gap-3">
											<div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
											<div>
												<span className="font-medium">IP-адреса сотрудников компании</span>
												<p className="text-gray-400 text-sm">Офисные и удаленные адреса вашей команды</p>
											</div>
										</li>
										<li className="flex items-start gap-3">
											<div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
											<div>
												<span className="font-medium">Партнерские сервисы и API</span>
												<p className="text-gray-400 text-sm">Внешние системы, которые легитимно обращаются к вашему сайту</p>
											</div>
										</li>
										<li className="flex items-start gap-3">
											<div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
											<div>
												<span className="font-medium">Поисковые системы</span>
												<p className="text-gray-400 text-sm">Google, Yandex, Bing и другие поисковые роботы</p>
											</div>
										</li>
										<li className="flex items-start gap-3">
											<div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
											<div>
												<span className="font-medium">Собственные мониторинговые системы</span>
												<p className="text-gray-400 text-sm">Сервисы проверки доступности и производительности</p>
											</div>
										</li>
									</ul>
								</div>

								<div>
									<h5 className="text-lg font-semibold mb-4 flex items-center gap-2">
										<FiX className="w-5 h-5 text-red-400" />
										Когда добавлять в черный список
									</h5>
									<ul className="space-y-3">
										<li className="flex items-start gap-3">
											<div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
											<div>
												<span className="font-medium">IP с повторными атаками</span>
												<p className="text-gray-400 text-sm">Адреса, которые неоднократно пытались атаковать ваш сайт</p>
											</div>
										</li>
										<li className="flex items-start gap-3">
											<div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
											<div>
												<span className="font-medium">Известные источники спама</span>
												<p className="text-gray-400 text-sm">IP из черных списков спамеров и ботов</p>
											</div>
										</li>
										<li className="flex items-start gap-3">
											<div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
											<div>
												<span className="font-medium">Страны с высокой активностью атак</span>
												<p className="text-gray-400 text-sm">Если вы не работаете с определенными регионами</p>
											</div>
										</li>
										<li className="flex items-start gap-3">
											<div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
											<div>
												<span className="font-medium">Конкурентные сканеры</span>
												<p className="text-gray-400 text-sm">IP, которые постоянно сканируют ваш сайт без легитимной цели</p>
											</div>
										</li>
									</ul>
								</div>
							</div>
						</div>
					</div>
				);

			case "configuration":
				return (
					<div className="space-y-12">
						<div className="space-y-6">
							<h3 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
								Вкладка Конфигурация
							</h3>
							<p className="text-gray-300 text-lg">
								Центр управления настройками защиты вашего сайта. Здесь вы можете тонко настроить все параметры
								системы защиты под конкретные требования вашего проекта.
							</p>
						</div>

						{/* Блок 1: Настройки чувствительности */}
						<div className="bg-white/5 backdrop-blur-lg rounded-3xl p-8 border border-white/10">
							<div className="flex items-start gap-4 mb-6">
								<div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl">
									<FiSettings className="w-6 h-6" />
								</div>
								<div>
									<h4 className="text-2xl font-bold mb-2">Настройки чувствительности</h4>
									<p className="text-gray-300">
										Регулируйте уровень чувствительности системы защиты с помощью ползунков.
										Настройка позволяет балансировать между безопасностью и доступностью для легитимных пользователей.
									</p>
								</div>
							</div>

							<div className="space-y-8">
								{/* Чувствительность прокси */}
								<div className="border-l-4 border-blue-500 pl-6 py-4">
									<div className="flex items-center gap-3 mb-4">
										<div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
											<FiActivity className="w-5 h-5 text-blue-400" />
										</div>
										<div>
											<h5 className="text-xl font-bold">Чувствительность прокси</h5>
											<p className="text-gray-400 text-sm">Чувствительность к количеству запросов в секунду для предотвращения DDoS атак</p>
										</div>
									</div>

									<div className="space-y-4">
										<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
											<div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
												<div className="flex items-center gap-2 mb-2">
													<div className="w-3 h-3 bg-red-500 rounded-full"></div>
													<span className="font-medium">Высокая чувствительность</span>
												</div>
												<p className="text-sm text-gray-300">
													Система будет агрессивно блокировать трафик при малейших признаках атаки.
													Рекомендуется для сайтов с критически важными данными.
												</p>
												<div className="mt-3 text-xs text-blue-400">
													🔒 Максимальная защита, возможны ложные срабатывания
												</div>
											</div>

											<div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
												<div className="flex items-center gap-2 mb-2">
													<div className="w-3 h-3 bg-green-500 rounded-full"></div>
													<span className="font-medium">Средняя чувствительность</span>
												</div>
												<p className="text-sm text-gray-300">
													Баланс между защитой и доступностью. Подходит для большинства коммерческих сайтов и интернет-магазинов.
												</p>
												<div className="mt-3 text-xs text-green-400">
													⚖️ Оптимальный баланс защиты и производительности
												</div>
											</div>

											<div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
												<div className="flex items-center gap-2 mb-2">
													<div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
													<span className="font-medium">Низкая чувствительность</span>
												</div>
												<p className="text-sm text-gray-300">
													Минимальное вмешательство в трафик. Используется для высоконагруженных проектов, где важна доступность.
												</p>
												<div className="mt-3 text-xs text-yellow-400">
													🚀 Максимальная производительность, меньше защиты
												</div>
											</div>
										</div>
									</div>
								</div>

								{/* Чувствительность AI */}
								<div className="border-l-4 border-purple-500 pl-6 py-4">
									<div className="flex items-center gap-3 mb-4">
										<div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
											<FiZap className="w-5 h-5 text-purple-400" />
										</div>
										<div>
											<h5 className="text-xl font-bold">Чувствительность AI-защиты</h5>
											<p className="text-gray-400 text-sm">Чувствительность искусственного интеллекта к определению подозрительных IP-адресов</p>
										</div>
									</div>

									<div className="space-y-4">
										<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
											<div>
												<h6 className="font-medium mb-3">Что анализирует AI:</h6>
												<ul className="space-y-2">
													<li className="flex items-start gap-2">
														<FiCheck className="w-4 h-4 text-green-400 mt-0.5" />
														<span className="text-sm">Необычные временные интервалы между запросами</span>
													</li>
													<li className="flex items-start gap-2">
														<FiCheck className="w-4 h-4 text-green-400 mt-0.5" />
														<span className="text-sm">Географические аномалии</span>
													</li>
													<li className="flex items-start gap-2">
														<FiCheck className="w-4 h-4 text-green-400 mt-0.5" />
														<span className="text-sm">Аномалии траффике сайта</span>
													</li>
												</ul>
											</div>

											<div>
												<h6 className="font-medium mb-3">Рекомендации по настройке:</h6>
												<div className="space-y-3">
													<div className="flex items-start gap-2">
														<div className="w-2 h-2 bg-red-500 rounded-full mt-1.5"></div>
														<div>
															<span className="text-sm font-medium">Высокая чувствительность</span>
															<p className="text-xs text-gray-400">Для сайтов с финансовыми операциями или персональными данными</p>
														</div>
													</div>
													<div className="flex items-start gap-2">
														<div className="w-2 h-2 bg-green-500 rounded-full mt-1.5"></div>
														<div>
															<span className="text-sm font-medium">Средняя чувствительность</span>
															<p className="text-xs text-gray-400">Для корпоративных сайтов и интернет-магазинов</p>
														</div>
													</div>
													<div className="flex items-start gap-2">
														<div className="w-2 h-2 bg-yellow-500 rounded-full mt-1.5"></div>
														<div>
															<span className="text-sm font-medium">Низкая чувствительность</span>
															<p className="text-xs text-gray-400">Для блогов, новостных сайтов и публичных ресурсов</p>
														</div>
													</div>
												</div>
											</div>
										</div>

										<div className="bg-gray-800/50 rounded-xl p-4">
											<p className="text-sm text-gray-300">
												<span className="font-medium text-purple-300">Важно:</span> AI-система обучается на поведении ваших пользователей.
												Рекомендуется начать со средних настроек и через неделю проанализировать статистику в разделе "Защита".
												При большом количестве ложных срабатываний — уменьшите чувствительность.
											</p>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Блок 2: Управление IP */}
						<div className="bg-white/5 backdrop-blur-lg rounded-3xl p-8 border border-white/10">
							<div className="flex items-start gap-4 mb-6">
								<div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl">
									<FiFilter className="w-6 h-6" />
								</div>
								<div>
									<h4 className="text-2xl font-bold mb-2">Управление IP-адресами</h4>
									<p className="text-gray-300">
										Полный контроль над белыми и черными списками IP-адресов.
										Вы можете добавлять и удалять адреса в списки разрешенных или запрещенных.
									</p>
								</div>
							</div>

							<div className="space-y-6">
								{/* Белый список */}
								<div>
									<h5 className="text-xl font-bold mb-4 flex items-center gap-3">
										<div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
											<FiCheck className="w-4 h-4 text-green-400" />
										</div>
										Белый список IP-адресов
									</h5>

									<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
										<div className="bg-green-500/10 rounded-xl p-5">
											<h6 className="font-semibold text-green-300 mb-3">Что попадает в белый список:</h6>
											<ul className="space-y-2">
												<li className="flex items-start gap-2">
													<FiCheck className="w-4 h-4 text-green-400 mt-0.5" />
													<span className="text-sm">IP-адреса ваших сотрудников</span>
												</li>
												<li className="flex items-start gap-2">
													<FiCheck className="w-4 h-4 text-green-400 mt-0.5" />
													<span className="text-sm">Сервера мониторинга и аналитики</span>
												</li>
												<li className="flex items-start gap-2">
													<FiCheck className="w-4 h-4 text-green-400 mt-0.5" />
													<span className="text-sm">Партнерские API и сервисы</span>
												</li>
											</ul>
										</div>
									</div>
								</div>

								{/* Черный список */}
								<div>
									<h5 className="text-xl font-bold mb-4 flex items-center gap-3">
										<div className="w-8 h-8 bg-red-500/20 rounded-lg flex items-center justify-center">
											<FiX className="w-4 h-4 text-red-400" />
										</div>
										Черный список IP-адресов
									</h5>

									<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
										<div className="bg-red-500/10 rounded-xl p-5">
											<h6 className="font-semibold text-red-300 mb-3">Что попадает в черный список:</h6>
											<ul className="space-y-2">
												<li className="flex items-start gap-2">
													<FiX className="w-4 h-4 text-red-400 mt-0.5" />
													<span className="text-sm">Известные источники атак и спама</span>
												</li>
												<li className="flex items-start gap-2">
													<FiX className="w-4 h-4 text-red-400 mt-0.5" />
													<span className="text-sm">IP с повторными нарушениями</span>
												</li>
												<li className="flex items-start gap-2">
													<FiX className="w-4 h-4 text-red-400 mt-0.5" />
													<span className="text-sm">Конкурентные сканеры и парсеры</span>
												</li>
												<li className="flex items-start gap-2">
													<FiX className="w-4 h-4 text-red-400 mt-0.5" />
													<span className="text-sm">Адреса из заблокированных стран</span>
												</li>
											</ul>
										</div>

										<div className="bg-gray-800/50 rounded-xl p-5">
											<h6 className="font-semibold mb-3">Приоритет списков:</h6>
											<p className="text-sm text-gray-300 mb-3">
												Система проверяет IP в следующем порядке:
											</p>
											<div className="space-y-2">
												<div className="flex items-center gap-3 p-2 bg-white/5 rounded">
													<div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-xs">1</div>
													<span className="text-sm">Белый список — разрешен всегда</span>
												</div>
												<div className="flex items-center gap-3 p-2 bg-white/5 rounded">
													<div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-xs">2</div>
													<span className="text-sm">Черный список — заблокирован всегда</span>
												</div>
												<div className="flex items-center gap-3 p-2 bg-white/5 rounded">
													<div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-xs">3</div>
													<span className="text-sm">Проверка по остальным правилам</span>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Блок 3: Защита от ботов */}
						<div className="bg-white/5 backdrop-blur-lg rounded-3xl p-8 border border-white/10">
							<div className="flex items-start gap-4 mb-6">
								<div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-2xl">
									<FiShield className="w-6 h-6" />
								</div>
								<div>
									<h4 className="text-2xl font-bold mb-2">Защита от ботов</h4>
									<p className="text-gray-300">
										Гибкая настройка защиты от автоматизированных скриптов и ботов.
										Вы можете включать/отключать защиту и настраивать исключения.
									</p>
								</div>
							</div>

							<div className="space-y-8">
								{/* Включение/выключение */}
								<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
									<div className="bg-white/5 rounded-xl p-5">
										<h5 className="text-lg font-bold mb-3 flex items-center gap-2">
											<div className="w-6 h-6 bg-green-500/20 rounded flex items-center justify-center">
												<FiCheck className="w-3 h-3 text-green-400" />
											</div>
											Включение защиты
										</h5>
										<p className="text-sm text-gray-300 mb-3">
											При включенной защите система будет проверять всех пользователей на роботов.
										</p>
									</div>

									<div className="bg-white/5 rounded-xl p-5">
										<h5 className="text-lg font-bold mb-3 flex items-center gap-2">
											<div className="w-6 h-6 bg-red-500/20 rounded flex items-center justify-center">
												<FiX className="w-3 h-3 text-red-400" />
											</div>
											Отключение защиты
										</h5>
										<p className="text-sm text-gray-300 mb-3">
											При отключенной защите все проверки пропускаются. Используйте только если:
										</p>
										<ul className="space-y-1">
											<li className="flex items-center gap-2 text-sm">
												<FiAlertTriangle className="w-3 h-3 text-yellow-400" />
												Тестирование производительности сайта
											</li>
											<li className="flex items-center gap-2 text-sm">
												<FiAlertTriangle className="w-3 h-3 text-yellow-400" />
												Временные технические работы
											</li>
											<li className="flex items-center gap-2 text-sm">
												<FiAlertTriangle className="w-3 h-3 text-yellow-400" />
												Очень специфичный трафик (например, только API)
											</li>
										</ul>
									</div>
								</div>

								{/* Разрешенные URL */}
								<div>
									<h5 className="text-xl font-bold mb-4">Разрешенные URL</h5>
									<p className="text-gray-300 mb-4">
										На указанных URL защита от ботов действовать не будет. Используйте для API-эндпоинтов,
										RSS-лент, файлов для скачивания и других ресурсов, к которым часто обращаются боты.
									</p>

									<div className="bg-gray-800/50 rounded-xl p-5">
										<h6 className="font-medium mb-3">Примеры разрешенных URL:</h6>
										<div className="space-y-2 font-mono text-sm">
											<div className="bg-black/30 p-3 rounded">
												<span className="text-green-400">/api/</span> <span className="text-gray-500 ml-2">// Все API-эндпоинты</span>
											</div>
											<div className="bg-black/30 p-3 rounded">
												<span className="text-green-400">/robots.txt</span> <span className="text-gray-500 ml-2">// Поисковые роботы</span>
											</div>
											<div className="bg-black/30 p-3 rounded">
												<span className="text-green-400">/sitemap.xml</span> <span className="text-gray-500 ml-2">// Карта сайта</span>
											</div>
											<div className="bg-black/30 p-3 rounded">
												<span className="text-green-400">/favicon.ico</span> <span className="text-gray-500 ml-2">// Фавикон сайта</span>
											</div>
											<div className="bg-black/30 p-3 rounded">
												<span className="text-green-400">/static/</span> <span className="text-gray-500 ml-2">// Статические файлы</span>
											</div>
										</div>
									</div>
								</div>

								{/* Разрешенные User-Agent */}
								<div>
									<h5 className="text-xl font-bold mb-4">Разрешенные User-Agent</h5>
									<p className="text-gray-300 mb-4">
										Укажите User-Agent, которые не будут проверяться на ботов.
										Используйте для поисковых роботов, валидаторов и других легитимных ботов.
									</p>

									<div className="bg-gray-800/50 rounded-xl p-5">
										<h6 className="font-medium mb-3">Примеры популярных разрешенных ботов:</h6>
										<div className="space-y-2">
											<div className="flex items-center justify-between p-3 bg-white/5 rounded">
												<code className="text-sm">Googlebot</code>
											</div>
											<div className="flex items-center justify-between p-3 bg-white/5 rounded">
												<code className="text-sm">YandexBot</code>
											</div>
											<div className="flex items-center justify-between p-3 bg-white/5 rounded">
												<code className="text-sm">Bingbot</code>
											</div>
										</div>

										<div className="mt-4">
											<p className="text-sm text-gray-400 mb-2">Можно добавить частичные совпадения:</p>
											<div className="font-mono text-sm bg-black/30 p-3 rounded">
												<span className="text-green-400">bot</span> <span className="text-gray-500 ml-2">// Все User-Agent содержащие "bot"</span><br />
												<span className="text-green-400">Google</span> <span className="text-gray-500 ml-2">// Все User-Agent начинающиеся с "Google"</span>
											</div>
										</div>
									</div>
								</div>
								{/* Примечание о предустановленных списках */}
								<div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-5">
									<div className="flex items-start gap-3">
										<FiInfo className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
										<div>
											<h6 className="font-semibold text-blue-300 mb-2">Примечание о предустановленных списках</h6>
											<p className="text-sm text-gray-300 mb-2">
												Система по умолчанию включает наиболее популярные легитимные URL (<code className="text-blue-300">/robots.txt</code>,
												<code className="text-blue-300"> /sitemap.xml</code>, <code className="text-blue-300"> /favicon.ico</code>) и User-Agent
												(<code className="text-blue-300">Googlebot</code>, <code className="text-blue-300">YandexBot</code>, <code className="text-blue-300">Bingbot</code>) в список разрешенных.
											</p>
											<p className="text-sm text-gray-300">
												Эти настройки оптимизированы для большинства сайтов и обеспечивают корректную работу с поисковыми системами.
												При необходимости вы можете удалить любые записи из этих списков для вашего домена через соответствующие настройки.
											</p>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Блок 4: Управление странами */}
						<div className="bg-white/5 backdrop-blur-lg rounded-3xl p-8 border border-white/10">
							<div className="flex items-start gap-4 mb-6">
								<div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-red-500 to-pink-500 rounded-2xl">
									<FiGlobe className="w-6 h-6" />
								</div>
								<div>
									<h4 className="text-2xl font-bold mb-2">Управление странами</h4>
									<p className="text-gray-300">
										Географический контроль доступа к вашему сайту. Блокируйте страны,
										из которых приходит нежелательный трафик.
									</p>
								</div>
							</div>

							<div className="space-y-6">
								{/* Как это работает */}
								<div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5">
									<h5 className="text-lg font-bold text-red-300 mb-3">Как работает блокировка по странам:</h5>
									<p className="text-sm text-gray-300 mb-3">
										При добавлении страны в список запрещенных, все запросы с IP-адресов,
										принадлежащих этой стране, будут автоматически блокироваться на уровне нашей сети.
										Запросы не доходят до вашего сервера, что экономит ресурсы и повышает безопасность.
									</p>
								</div>

								{/* Примеры использования */}
								<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
									<div className="bg-white/5 rounded-xl p-5">
										<h6 className="font-semibold mb-3">Когда блокировать страны:</h6>
										<ul className="space-y-2">
											<li className="flex items-start gap-2">
												<div className="w-2 h-2 bg-red-500 rounded-full mt-1.5"></div>
												<span className="text-sm">Из страны приходит много спама и атак</span>
											</li>
											<li className="flex items-start gap-2">
												<div className="w-2 h-2 bg-red-500 rounded-full mt-1.5"></div>
												<span className="text-sm">Ваш бизнес не работает в определенном регионе</span>
											</li>
											<li className="flex items-start gap-2">
												<div className="w-2 h-2 bg-red-500 rounded-full mt-1.5"></div>
												<span className="text-sm">Требования законодательства (GDPR, санкции)</span>
											</li>
											<li className="flex items-start gap-2">
												<div className="w-2 h-2 bg-red-500 rounded-full mt-1.5"></div>
												<span className="text-sm">Большое количество ботнет-активности из региона</span>
											</li>
										</ul>
									</div>

									<div className="bg-white/5 rounded-xl p-5">
										<h6 className="font-semibold mb-3">Рекомендации:</h6>
										<ul className="space-y-3">
											<li className="flex items-start gap-2">
												<FiAlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5" />
												<span className="text-sm">Не блокируйте страны, где находятся ваши клиенты</span>
											</li>
											<li className="flex items-start gap-2">
												<FiAlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5" />
												<span className="text-sm">Проверьте статистику трафика перед блокировкой</span>
											</li>
											<li className="flex items-start gap-2">
												<FiAlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5" />
												<span className="text-sm">Используйте временную блокировку для тестирования</span>
											</li>
										</ul>
									</div>
								</div>

								{/* Как добавить/удалить */}
							</div>
						</div>

						{/* Заключительная информация */}
						<div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-3xl p-8 border border-white/10">
							<div className="text-center">
								<h4 className="text-2xl font-bold mb-4">Рекомендации по настройке</h4>
								<p className="text-gray-300 mb-6 max-w-3xl mx-auto">
									Начинайте с умеренных настроек и постепенно корректируйте их на основе статистики
									из раздела "Защита". Регулярно просматривайте заблокированные запросы, чтобы
									минимизировать ложные срабатывания и максимизировать защиту.
								</p>

								<div className="flex flex-col sm:flex-row gap-4 justify-center">
									<a
										href="/dashboard"
										className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-purple-500 hover:to-blue-500 rounded-xl font-semibold transition-all duration-300 hover:scale-105 flex items-center gap-2"
									>
										<FiSettings className="w-5 h-5" />
										Перейти к настройкам
									</a>
									<a
										href="#protection"
										onClick={() => setActiveTab('protection')}
										className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-semibold transition-all duration-300 flex items-center gap-2"
									>
										<FiShield className="w-5 h-5" />
										Анализировать статистику
									</a>
								</div>
							</div>
						</div>
					</div>
				);

			default:
				return null;
		}
	};

	return (
		<>
			<Seo
				title={`${serviceName} - Документация`}
				description="Подробная документация по использованию сервиса защиты и ускорения сайтов"
			/>

			<div className="relative min-h-screen bg-gray-900 text-white">
				{/* Hero секция */}
				<section className="relative z-10 py-20 px-4">
					<div className="max-w-7xl mx-auto">
						<motion.div
							className="text-center mb-12"
							initial={{ opacity: 0, y: -40 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 1 }}
						>
							<div className="flex justify-center mb-6">
								<div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-3xl flex items-center justify-center">
									<FiBook className="w-10 h-10" />
								</div>
							</div>
							<h1 className="text-5xl md:text-6xl font-extrabold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400">
								Документация {serviceName}
							</h1>
							<p className="text-xl text-gray-300 max-w-3xl mx-auto">
								Подробные инструкции и руководства по использованию всех функций сервиса
							</p>
						</motion.div>

						{/* Вкладки */}
						<div className="max-w-4xl mx-auto">
							<div className="flex flex-wrap justify-center gap-2 mb-8">
								{tabs.map((tab) => (
									<button
										key={tab.id}
										onClick={() => setActiveTab(tab.id)}
										className={`flex items-center gap-3 px-6 py-4 rounded-2xl font-semibold transition-all duration-300 ${activeTab === tab.id
											? 'bg-gradient-to-r from-blue-500 to-purple-600 shadow-2xl shadow-purple-500/30'
											: 'bg-white/5 hover:bg-white/10'
											}`}
									>
										{tab.icon}
										{tab.label}
									</button>
								))}
							</div>

							{/* Уведомление о копировании */}
							{copiedText && (
								<motion.div
									initial={{ opacity: 0, y: 10 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0, y: 10 }}
									className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-3 rounded-xl shadow-2xl z-50 flex items-center gap-2"
								>
									<FiCheck className="w-5 h-5" />
									<span>Скопировано: {copiedText}</span>
								</motion.div>
							)}

							{/* Контент вкладки */}
							<motion.div
								key={activeTab}
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={{ duration: 0.3 }}
								className="min-h-[600px]"
							>
								{renderTabContent()}
							</motion.div>

							{/* Навигация между разделами */}
							<div className="mt-12 pt-8 border-t border-white/10">
								<div className="flex justify-between items-center">
									<div>
										<p className="text-gray-400 text-sm">Нужна дополнительная помощь?</p>
										<a href="https://t.me/Shield_AI" className="text-blue-400 hover:text-blue-300 transition-colors">
											Обратиться в поддержку →
										</a>
									</div>
									<div className="flex items-center gap-4">
										{tabs.map((tab, index) => {
											const currentIndex = tabs.findIndex(t => t.id === activeTab);
											if (index === currentIndex - 1) {
												return (
													<button
														key={tab.id}
														onClick={() => setActiveTab(tab.id)}
														className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
													>
														← {tab.label}
													</button>
												);
											}
											if (index === currentIndex + 1) {
												return (
													<button
														key={tab.id}
														onClick={() => setActiveTab(tab.id)}
														className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
													>
														{tab.label} →
													</button>
												);
											}
											return null;
										})}
									</div>
								</div>
							</div>
						</div>
					</div>
				</section>
			</div>
		</>
	);
};

export default Documentation;
